import React from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { getUnitForOpenMeteoParameter } from '../../utils/units';

interface TimeSeriesChartProps {
  data: any[];
  parameter: string;
}

export function TimeSeriesChart({ data, parameter }: TimeSeriesChartProps) {
  const chartData = React.useMemo(() => {
    if (!data || data.length === 0) return [];
    
    // For single point data (when a grid cell is selected)
    if (data.length === 1) {
      const point = data[0];
      if (!Array.isArray(point.times) || !Array.isArray(point.values)) return [];
      
      return point.times.map((time: string, index: number) => ({
        time: time.split('T')[0],
        value: point.values[index]
      })).filter(d => d.value !== null && !isNaN(d.value));
    }

    // For multiple points (average across all points)
    const timeMap = new Map<string, number[]>();
    
    data.forEach(point => {
      if (!Array.isArray(point.times) || !Array.isArray(point.values)) return;
      
      point.times.forEach((time: string, index: number) => {
        const value = point.values[index];
        if (value === null || value === undefined || isNaN(value)) return;
        
        const dateKey = time.split('T')[0];
        if (!timeMap.has(dateKey)) {
          timeMap.set(dateKey, []);
        }
        timeMap.get(dateKey)?.push(value);
      });
    });

    return Array.from(timeMap.entries())
      .map(([time, values]) => ({
        time,
        value: values.reduce((sum, val) => sum + val, 0) / values.length
      }))
      .sort((a, b) => a.time.localeCompare(b.time));
  }, [data]);

  if (chartData.length === 0) {
    return (
      <div className="h-full flex items-center justify-center text-[var(--secondary-text)]">
        No valid data available for the chart
      </div>
    );
  }

  const unit = getUnitForOpenMeteoParameter(parameter);

  return (
    <div className="h-full">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={chartData}>
          <XAxis 
            dataKey="time" 
            stroke="var(--secondary-text)"
            tick={{ fill: 'var(--secondary-text)' }}
          />
          <YAxis 
            stroke="var(--secondary-text)"
            tick={{ fill: 'var(--secondary-text)' }}
            label={{ 
              value: unit, 
              angle: -90, 
              position: 'insideLeft',
              fill: 'var(--secondary-text)'
            }}
          />
          <Tooltip
            contentStyle={{
              background: 'var(--secondary-bg)',
              border: '1px solid var(--border-color)',
              color: 'var(--primary-text)'
            }}
            formatter={(value: number) => [`${value.toFixed(2)}${unit}`, 'Value']}
            labelFormatter={(label) => label}
          />
          <Line
            type="monotone"
            dataKey="value"
            stroke="var(--accent-color)"
            strokeWidth={2}
            dot={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}